import { useShadow, useShadowStyle } from '../wcUtil.ts';
import '../base/buttons/as-button.ts';
import styleText from './as-modal-export-progress.css?raw';
import type { ExportStatusDetail, ExportProgressDetail } from '../../data/data-interfaces.external.ts';

export class AsModalExportProgress extends HTMLElement {
  #cancelTimer: ReturnType<typeof setTimeout> | null = null;

  private _onExportStatus = (e: Event): void => {
    const detail = (e as CustomEvent<ExportStatusDetail>).detail;
    if (detail.state === 'pending') {
      this._reset();
      this._overlay()?.open(this.id);
    } else if (detail.state === 'success' && detail.operation !== 'grabcad') {
      this._clearCancelTimer();
      this._overlay()?.close();
    } else if (detail.state === 'error') {
      this._setError(detail.message);
    }
    // grabcad success: as-modal-grabcad-success opens itself on the same event,
    // which switches the overlay's visible-child away from this panel.
  };

  private _onExportProgress = (e: Event): void => {
    const { current, total } = (e as CustomEvent<ExportProgressDetail>).detail;
    this._setProgress(current, total);
  };

  connectedCallback(): void {
    useShadow(this, /*html*/`
      <div class="modal-panel">
        <p class="heading">Exporting…</p>
        <progress class="progress-bar" value="0" max="1"></progress>
        <p class="progress-label"></p>
        <p class="error-msg" hidden></p>
        <div class="footer-actions">
          <as-button name="cancel" default-text="Cancel export" disabled></as-button>
        </div>
      </div>
    `);
    useShadowStyle(this, styleText, './as-modal-export-progress.css');
    this.shadowRoot!.addEventListener('as-button-click', () => {
      this._clearCancelTimer();
      this._overlay()?.close();
    });
    window.addEventListener('as-export-status', this._onExportStatus);
    window.addEventListener('as-export-progress', this._onExportProgress);
  }

  disconnectedCallback(): void {
    this._clearCancelTimer();
    window.removeEventListener('as-export-status', this._onExportStatus);
    window.removeEventListener('as-export-progress', this._onExportProgress);
  }

  private _overlay(): any {
    return this.closest('as-modal-overlay') ?? document.querySelector('#as-app-overlay');
  }

  private _clearCancelTimer(): void {
    if (this.#cancelTimer !== null) {
      clearTimeout(this.#cancelTimer);
      this.#cancelTimer = null;
    }
  }

  private _setCancelEnabled(enabled: boolean): void {
    const btn = this.shadowRoot?.querySelector('as-button') as HTMLElement | null;
    if (!btn) return;
    if (enabled) btn.removeAttribute('disabled');
    else btn.setAttribute('disabled', '');
  }

  private _reset(): void {
    const sr = this.shadowRoot;
    if (!sr) return;
    (sr.querySelector('.heading') as HTMLElement).textContent = 'Exporting…';
    const bar = sr.querySelector('.progress-bar') as HTMLProgressElement;
    bar.value = 0;
    bar.max = 1;
    bar.hidden = false;
    (sr.querySelector('.progress-label') as HTMLElement).textContent = '';
    (sr.querySelector('.progress-label') as HTMLElement).hidden = false;
    (sr.querySelector('.error-msg') as HTMLElement).hidden = true;
    this._setCancelEnabled(false);
    this._clearCancelTimer();
    this.#cancelTimer = setTimeout(() => this._setCancelEnabled(true), 5000);
  }

  private _setProgress(current: number, total: number): void {
    const sr = this.shadowRoot;
    if (!sr) return;
    const bar = sr.querySelector('.progress-bar') as HTMLProgressElement;
    const label = sr.querySelector('.progress-label') as HTMLElement;
    if (bar) { bar.value = current; bar.max = Math.max(total, 1); }
    if (label) label.textContent = total > 0 ? `${current} of ${total} file${total !== 1 ? 's' : ''}` : '';
  }

  private _setError(message?: string): void {
    const sr = this.shadowRoot;
    if (!sr) return;
    this._clearCancelTimer();
    (sr.querySelector('.heading') as HTMLElement).textContent = 'Export failed';
    (sr.querySelector('.progress-bar') as HTMLElement).hidden = true;
    (sr.querySelector('.progress-label') as HTMLElement).hidden = true;
    const errEl = sr.querySelector('.error-msg') as HTMLElement;
    errEl.textContent = message ?? 'An error occurred during export.';
    errEl.hidden = false;
    this._setCancelEnabled(true);
  }
}

if (!customElements.get('as-modal-export-progress')) {
  customElements.define('as-modal-export-progress', AsModalExportProgress);
}
